import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const faqs = [
  {
    q: "51吃瓜网站多久更新一次内容？",
    a: "本站每天上午 8:00 起陆续更新，重要热点会在全天持续补充，编辑精选的焦点内容会置顶在首页轮播。",
  },
  {
    q: "站内内容是否都可以转载？",
    a: "本站为资讯演示站点，内容均为示例创作，仅用于产品展示。如需引用，请注明来源并遵守相关规范。",
  },
  {
    q: "搜索和筛选功能为什么说“暂不完全”？",
    a: "当前搜索、筛选与分页均基于站内前端演示数据运行，尚未接入真实后端接口，因此结果以演示为主。",
  },
  {
    q: "如何向我们提供内容线索？",
    a: "可以通过下方的反馈表单留言，选择“内容建议”或“商务合作”，我们会定期查看并回复。",
  },
];

const stats = [
  { value: "4 大", label: "资讯分类" },
  { value: "每日", label: "持续更新" },
  { value: "20+", label: "精选示例资讯" },
  { value: "0", label: "注册门槛" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 51吃瓜网站",
    description:
      "了解51吃瓜网站的定位、宗旨与联系方式。我们聚焦热点新闻、娱乐八卦与社会动态，为你整理每日新鲜吃瓜信息。",
    keywords: "51吃瓜网站,关于我们,热点新闻,娱乐八卦,联系方式",
  });

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <header className="mt-5 overflow-hidden rounded-lg bg-secondary-950 p-6 md:p-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-line"></i> 关于我们
              </span>
              <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
                关于51吃瓜网站
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-secondary-200 md:text-base">
                51吃瓜网站是一个聚焦热点新闻、娱乐八卦与社会动态的资讯平台。我们希望用轻松、清晰的方式，
                帮你把每天刷屏的热点梳理成一份好读的“吃瓜清单”。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-2">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg bg-secondary-900 px-4 py-3">
                  <p className="font-heading text-xl font-black text-primary-400">{s.value}</p>
                  <p className="mt-1 text-xs text-secondary-300">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </header>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
          <div className="flex flex-col gap-10">
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-live-line text-primary-500"></i> 网站简介
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                我们关注每一位活跃在社交平台上的普通用户：你可能没时间逐条刷完热搜，
                但又不希望错过真正有意思的消息。51吃瓜网站把明星动态、热点新闻、网络热议和社会资讯
                分成四个清晰的板块，用标题、摘要与分类标签帮你快速判断“这条值不值得点开”。
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                在内容呈现上，我们力求做到层级清晰、阅读轻松：焦点资讯用大幅配图突出，
                分类入口一目了然，列表页支持按时间与热度筛选，详情页则保留舒适的阅读节奏与宽松的行距。
              </p>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-eye-line text-accent-500"></i> 发展愿景
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  {
                    icon: "ri-timer-flash-line",
                    title: "更快",
                    desc: "让热点资讯的第一时间触达变得简单，减少信息梳理的成本。",
                  },
                  {
                    icon: "ri-shield-check-line",
                    title: "更清",
                    desc: "坚持标注来源与分类，理性吃瓜，不做耸动的标题党。",
                  },
                  {
                    icon: "ri-heart-3-line",
                    title: "更好读",
                    desc: "用克制、舒适的排版，让连续阅读不再疲惫。",
                  },
                  {
                    icon: "ri-community-line",
                    title: "更有趣",
                    desc: "把网络热梗与生活话题整理成轻松的谈资与共鸣。",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-question-line text-primary-500"></i> 常见问题
              </h2>
              <div className="mt-4 flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {faqs.map((faq) => (
                  <div key={faq.q} className="p-5">
                    <h4 className="flex items-start gap-2 font-heading text-base font-bold text-foreground-950">
                      <span className="text-primary-500">Q</span>
                      {faq.q}
                    </h4>
                    <p className="mt-2 pl-5 text-sm leading-relaxed text-foreground-600">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-contacts-book-line text-accent-500"></i>
                联系方式
              </h3>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-500"></i>
                  contact@51chigua.com
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-wechat-line text-primary-500"></i>
                  微信公众号：51吃瓜网站
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-500"></i>
                  工作时间：每天 08:00 - 22:00
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-accent-500"></i>
                使用说明
              </h3>
              <ol className="mt-4 flex list-decimal flex-col gap-2.5 pl-5 text-sm leading-relaxed text-foreground-600">
                <li>在顶部导航选择感兴趣的分类，或直接进入首页浏览焦点内容。</li>
                <li>分类页可通过“时间范围”和“排序方式”快速筛选资讯。</li>
                <li>点击任意卡片进入详情页，底部还有相关推荐可以继续阅读。</li>
                <li>右上角搜索按钮支持按关键词查找站内资讯（演示功能）。</li>
              </ol>
            </div>
          </aside>
        </div>

        <section className="mt-10">
          <FeedbackForm />
        </section>

        <p className="mt-6 text-center text-xs text-foreground-400">
          本站为资讯演示站点，所有内容均为示例创作，不代表真实事件或人物，请理性阅读。
        </p>
      </div>
    </div>
  );
}